import {useUnloadingMethod} from '@hconnect/tuc-processes/fields/unloadingMethod/useUnloadingMethod'
import {Typography, Box, Radio, RadioGroup, FormControlLabel, FormControl} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useRmcCountryFeatures} from '../../../hooks/useRmcCountryFeatures'

import {PumpConfigurationOptions} from './Pump/PumpConfigurationOptions'
import {UnloadingMethodList} from './UnloadingMethodList'

/**
 * Component for the Unloading Method step
 * Contains a Yes/No toggle for pump ordering and conditional unloading method selection
 */
export const UnloadingMethodStep: React.FC = () => {
  const {t} = useTranslation()
  const {
    selectedUnloadingMethod,
    selectedPump,
    availableUnloadingMethods,
    setUnloadingMethodCode,
    setPumpType,
    configuration
  } = useUnloadingMethod()

  // Get region features to conditionally render parts of the component
  const regionFeatures = useRmcCountryFeatures()

  // Local state to track whether user wants to order a pump
  const {pump: pumpConfiguration, unloadingMethod: unloadingMethodConfiguration} = configuration

  // If the feature is not enabled, don't render anything
  if (!unloadingMethodConfiguration.isEnabled) {
    return null
  }

  const handleToggleChange = (needsPump: boolean) => {
    if (needsPump && pumpConfiguration.isEnabled) {
      // When switching to "Yes", set default pump configuration if available
      if (pumpConfiguration.isEnabled) {
        setPumpType(pumpConfiguration.pumpTypes.default)
      }
    } else {
      // When switching to "No", clear pump configuration and unloading method
      setPumpType(undefined)
    }
  }

  const handleUnloadingMethodSelect = (code: string) => {
    setUnloadingMethodCode(code)
  }

  return (
    <Box sx={{width: '100%', padding: 0}}>
      {/* Need to order a pump section - only shown if hasPumpSelector is enabled */}
      {regionFeatures.hasPumpSelector && (
        <Box sx={{mb: 4}}>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '20px',
              color: '#29333d',
              marginBottom: '24px'
            }}
          >
            {t('orderIntake.rmc.steps.unloading.pumpQuestion', 'Need to order a pump with us')}
          </Typography>

          <FormControl component="fieldset" sx={{width: '100%'}}>
            <RadioGroup
              value={selectedPump ? 'yes' : 'no'}
              onChange={(event) => handleToggleChange(event.target.value === 'yes')}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '16px',
                width: '100%'
              }}
              data-test-id="pump-toggle-group"
            >
              <FormControlLabel
                value="no"
                control={<Radio sx={{display: 'none'}} />}
                label={
                  <Box
                    sx={{
                      border: !selectedPump ? '2px solid #016ad4' : '1px solid #d1d9e0',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      backgroundColor: !selectedPump ? '#f2f8fd' : 'transparent',
                      width: '100%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '8px',
                      position: 'relative'
                    }}
                  >
                    <Radio
                      checked={!selectedPump}
                      sx={{
                        color: '#d1d9e0',
                        '&.Mui-checked': {
                          color: '#016ad4'
                        },
                        padding: 0,
                        pointerEvents: 'none'
                      }}
                      data-test-id="pump-toggle-no-radio"
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#29333d'
                      }}
                    >
                      {t('orderIntake.rmc.steps.unloading.no', 'No')}
                    </Typography>
                  </Box>
                }
                sx={{
                  margin: 0,
                  flex: 1,
                  '& .MuiFormControlLabel-label': {
                    padding: 0,
                    width: '100%'
                  }
                }}
              />
              <FormControlLabel
                value="yes"
                control={<Radio sx={{display: 'none'}} />}
                label={
                  <Box
                    sx={{
                      border: selectedPump ? '2px solid #016ad4' : '1px solid #d1d9e0',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      backgroundColor: selectedPump ? '#f2f8fd' : 'transparent',
                      width: '100%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '8px',
                      position: 'relative'
                    }}
                  >
                    <Radio
                      checked={!!selectedPump}
                      sx={{
                        color: '#d1d9e0',
                        '&.Mui-checked': {
                          color: '#016ad4'
                        },
                        padding: 0,
                        pointerEvents: 'none'
                      }}
                      data-test-id="pump-toggle-yes-radio"
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#29333d'
                      }}
                    >
                      {t('orderIntake.rmc.steps.unloading.yes', 'Yes')}
                    </Typography>
                  </Box>
                }
                sx={{
                  margin: 0,
                  flex: 1,
                  '& .MuiFormControlLabel-label': {
                    padding: 0,
                    width: '100%'
                  }
                }}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}

      {/* Unloading Method List - shown only when "No" is selected */}
      {!selectedPump && availableUnloadingMethods.length > 0 && (
        <UnloadingMethodList
          selectedUnloadingMethod={selectedUnloadingMethod}
          onUnloadingMethodSelect={handleUnloadingMethodSelect}
          availableUnloadingMethods={availableUnloadingMethods}
          pumpEnabled={pumpConfiguration.isEnabled}
          data-test-id="unloading-method-list"
        />
      )}

      {/* Detailed Pump Configuration - shown when "Yes" is selected */}
      {selectedPump && <PumpConfigurationOptions />}
    </Box>
  )
}
